import * as Popover from "@radix-ui/react-popover";
import { cva } from "class-variance-authority";
import * as React from "react";

import { faChevronDown } from "@performyard/fontawesome/pro-regular-svg-icons";

import { MultipleSelectDropdown } from "../components/MultipleSelectDropdown";
import { ChipsInput } from "./ChipsInput";

import { Option } from "../../../../types/Option";

import { useSelectBase } from "../../../../hooks";

import { cn } from "../../../../lib/utils";

import { Checkbox } from "../../../atoms/Checkbox";
import { Icon } from "../../../atoms/Icon";
import { useInputField } from "../../InputWrapper/InputContext";

const renderCheckboxOption = <T,>(option: Option<T>, isSelected: boolean): React.ReactNode => {
	return (
		<div className="flex items-center gap-2">
			<Checkbox
				id={`checkbox-${String(option.value)}`}
				checked={isSelected}
				size="small"
				disabled={option.disabled}
				aria-label={typeof option.label === "string" ? option.label : undefined}
			/>
			<div className="flex min-w-0 flex-col">
				<div className="truncate">{option.label}</div>
				{option.subtext && <div className="truncate text-caption text-neutral-5">{option.subtext}</div>}
			</div>
		</div>
	);
};

const containerVariants = cva(
	"group relative box-border flex items-center rounded-1 border border-solid border-neutral-3 bg-neutral-0",
	{
		variants: {
			errored: {
				true: "border-errored",
			},
			disabled: {
				true: "cursor-not-allowed opacity-disabled",
				false: "cursor-default hover:border-neutral-5 active:border-neutral-5",
			},
			focused: {
				true: "border-neutral-5",
			},
			displayVariant: {
				chips: "h-full min-h-8",
				checkbox: "h-8",
			},
		},
		defaultVariants: {
			errored: false,
			disabled: false,
			focused: false,
			displayVariant: "chips",
		},
	}
);

type MultipleSelectInputProps<T> = {
	/**
	 * Array of options to display in the dropdown. If not provided and `fetchOptions` is used, options will be fetched dynamically.
	 */
	options?: Option<T>[];
	/**
	 * Async function to fetch options based on search term. When provided, enables server-side search and filtering.
	 * @param searchTerm - The current search term entered by the user
	 * @returns Promise that resolves to an array of options matching the search term
	 */
	fetchOptions?: (searchTerm: string) => Promise<Option<T>[]>;
	/**
	 * Currently selected values array. Controlled component - parent manages state.
	 */
	value: T[];
	/**
	 * Callback function called when selection changes.
	 * @param value - The new array of selected values
	 */
	onChange: (value: T[]) => void;
	/**
	 * Optional callback function called when search value changes (combobox variant only).
	 * @param searchValue - The current search term
	 */
	onSearchChange?: (searchValue: string) => void;
	/**
	 * Whether the input is in an error state (displays red border).
	 * @default false
	 */
	errored?: boolean;
	/**
	 * Whether the input is disabled (not interactive, grayed out).
	 * @default false
	 */
	disabled?: boolean;
	/**
	 * Selection variant mode.
	 * - "select": Predefined list with keyboard navigation, no typeahead (default)
	 * - "combobox": Searchable list with filtering and virtualization
	 * @default "select"
	 */
	selectionVariant?: "select" | "combobox";
	/**
	 * Display variant for selected items.
	 * - "chips": Selected items shown as removable chips (default)
	 * - "checkbox": Dropdown options shown with checkboxes; trigger displays placeholder text
	 * @default "chips"
	 */
	displayVariant?: "chips" | "checkbox";
	/**
	 * Placeholder text shown when no options are selected.
	 * @default "Select options..."
	 */
	placeholder?: string;
	/**
	 * Unique identifier for the input element. If not provided, will use context ID from InputWrapper or generate one.
	 */
	id?: string;
	/**
	 * Test identifier for the input element (used for testing).
	 */
	"data-testid"?: string;
	/**
	 * Custom render function for displaying selected values as chips.
	 * @param value - The selected value to render
	 * @param onDismiss - Optional callback function to remove this value (undefined if disabled)
	 * @returns React node to render as a chip
	 */
	renderValue?: (value: T, onDismiss?: () => void) => React.ReactNode;
	/**
	 * Custom render function for displaying options in the dropdown.
	 * @param option - The option to render
	 * @param isSelected - Whether this option is currently selected
	 * @returns React node to render for the option
	 */
	renderOption?: (option: Option<T>, isSelected: boolean) => React.ReactNode;
	/**
	 * Optional CSS classes to apply to the component container (use with caution).
	 */
	dangerouslySetClassName?: string;
	/**
	 * Configuration for virtualized rendering (combobox variant only).
	 */
	virtualizationConfig?: {
		/**
		 * Height of each virtualized item in pixels.
		 * @default 32
		 */
		itemHeight?: number;
		/**
		 * Maximum height of the dropdown container in pixels.
		 * @default 240
		 */
		maxHeight?: number;
	};
	/**
	 * Preferred position for the dropdown to appear relative to the trigger.
	 * Radix UI will automatically flip to the opposite side if there's not enough space.
	 * @default "bottom"
	 */
	position?: "top" | "right" | "bottom" | "left";
	/**
	 * Maximum number of selections allowed. When the limit is reached, the dropdown
	 * will not open and no new selections can be made. Existing selections can still
	 * be removed via chip dismissal, which re-enables the dropdown.
	 */
	maxSelections?: number;
	/**
	 * Accessible label for screen readers when no visible label is present.
	 */
	ariaLabel?: string;
};

const MultipleSelectInput = <T,>({
	options: externalOptions = [],
	fetchOptions,
	value,
	onChange,
	onSearchChange,
	errored,
	disabled,
	selectionVariant = "select",
	displayVariant = "chips",
	placeholder = "Select options...",
	id,
	"data-testid": dataTestId,
	renderValue,
	renderOption,
	dangerouslySetClassName,
	virtualizationConfig,
	position = "bottom",
	maxSelections,
	ariaLabel,
}: MultipleSelectInputProps<T>) => {
	const { id: contextId } = useInputField();
	const hasAssociatedLabel = !!contextId;

	const isCheckbox = displayVariant === "checkbox";

	const result = useSelectBase<T>({
		mode: "multiple",
		options: externalOptions,
		fetchOptions,
		value,
		onChange,
		onSearchChange,
		errored,
		disabled,
		selectionVariant,
		virtualizationConfig,
		id,
		maxSelections,
		selectionBehavior: isCheckbox ? "toggle" : "accumulate",
	});

	if (result.mode !== "multiple") {
		throw new Error("Expected multiple mode");
	}

	const {
		inputId,
		hasError,
		isDisabled,
		isOpen,
		searchValue,
		isLoading,
		selectedOptions,
		filteredOptions,
		options,
		triggerWidth,
		triggerRef,
		searchInputRef,
		parentRef,
		virtualizer,
		containerHeight,
		focusedIndex,
		isKeyboardNavActive,
		handleKeyDown,
		handleItemMouseEnter,
		handleItemMouseLeave,
		setSearchValue,
		handleOpenChange,
		handleItemSelect,
	} = result;

	const isLimitReached = maxSelections !== undefined && value.length >= maxSelections;

	const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
		if (isDisabled) return;

		switch (e.key) {
			case "ArrowDown":
			case "ArrowUp":
				if (!isOpen) {
					e.preventDefault();
					handleOpenChange(true);
				} else {
					e.preventDefault();
					e.stopPropagation();
					if (parentRef.current) {
						parentRef.current.focus();
						handleKeyDown(e);
					}
				}
				break;
			case "Enter":
			case " ":
				e.preventDefault();
				if (!isOpen) {
					handleOpenChange(true);
				}
				break;
			case "Escape":
				if (isOpen) {
					e.preventDefault();
					e.stopPropagation();
					handleOpenChange(false);
				}
				break;
		}
	};

	const effectiveRenderOption = isCheckbox ? renderCheckboxOption : renderOption;

	return (
		<div className={cn("relative w-full", dangerouslySetClassName)}>
			<Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
				<Popover.Trigger asChild>
					<div
						ref={triggerRef}
						className={cn(
							containerVariants({
								errored: hasError,
								disabled: isDisabled,
								focused: isOpen,
								displayVariant,
							})
						)}
						onKeyDown={handleTriggerKeyDown}
						role="button"
						tabIndex={isDisabled ? -1 : 0}
						aria-expanded={isOpen}
						aria-haspopup="listbox"
						aria-labelledby={ariaLabel || !hasAssociatedLabel ? undefined : `label-${inputId}`}
						aria-label={ariaLabel}
					>
						<ChipsInput
							selectedOptions={isCheckbox ? [] : selectedOptions}
							value={value}
							onChange={onChange}
							isDisabled={isDisabled}
							placeholder={placeholder}
							inputId={inputId}
							dataTestId={dataTestId}
							renderValue={isCheckbox ? undefined : renderValue}
						/>

						<div className="flex shrink-0 items-center p-2 pl-0">
							<div className="w-4" />
						</div>
					</div>
				</Popover.Trigger>

				<div className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center">
					<Icon icon={faChevronDown} size={16} color="neutral-5" />
				</div>

				<MultipleSelectDropdown
					triggerWidth={triggerWidth}
					selectionVariant={selectionVariant}
					searchValue={searchValue}
					onSearchChange={(newSearchValue: string) => {
						setSearchValue(newSearchValue);
						onSearchChange?.(newSearchValue);
					}}
					isLoading={isLoading}
					filteredOptions={filteredOptions}
					options={options}
					value={value}
					renderOption={effectiveRenderOption}
					isDisabled={isDisabled}
					onItemSelect={handleItemSelect}
					fetchOptions={fetchOptions}
					searchInputRef={searchInputRef}
					parentRef={parentRef}
					virtualizer={virtualizer}
					containerHeight={containerHeight}
					focusedIndex={focusedIndex}
					isKeyboardNavActive={isKeyboardNavActive}
					handleKeyDown={handleKeyDown}
					handleItemMouseEnter={handleItemMouseEnter}
					handleItemMouseLeave={handleItemMouseLeave}
					virtualizationConfig={virtualizationConfig}
					position={position}
					isLimitReached={isLimitReached}
				/>
			</Popover.Root>
		</div>
	);
};

MultipleSelectInput.displayName = "MultipleSelectInput";

export { MultipleSelectInput, type MultipleSelectInputProps };
